import { Alert } from '@creditornot/cb-alert';
import { Button } from '@creditornot/cb-button';
import { colors, cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { ModalContent, ModalFooter, ModalHeader } from '@creditornot/cbt-modal';
import { ValidationErrors } from 'final-form';
import { DateTime } from 'luxon';
import React from 'react';
import { Field } from 'react-final-form';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { MOBILE_BREAKPOINT } from 'common/hooks/useIsMobile';
import { Typography } from 'common/typography';

import DateTimePicker from './DateTimePicker/DateTimePicker';
import SchedulesSection from './SchedulesSection/SchedulesSection';
import { SCHEDULING_TIMEFRAMES } from './constants';
import { SchedulingFormValues } from './types';
import CustomRadioCard from '../CustomRadioCard';

const StyledWrapper = styled.div`
  min-width: 800px;
  border-radius: 8px;
  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    min-width: auto;
    width: 98vw;
  }
`;

const StyledContentWrapper = styled(ModalContent)`
  padding: 0;
`;

const StyledSectionContent = styled.div`
  display: flex;
  align-content: center;
  align-items: center;
  gap: 8px;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const StyledSection = styled.div`
  padding: 24px;

  &:not(:last-of-type) {
    border-block-end: 1px solid ${cbSemanticCssVars.borderPrimary};
  }
`;

const StyledTitle = styled(Typography.Body2).attrs({ fontWeight: 'bold' })`
  margin-block-end: 16px;
  display: block;
`;

const StyledFooter = styled(ModalFooter)`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

const StyledErrorMessage = styled(Alert).attrs({ variant: 'error' })`
  background-color: white;
  color: ${colors.strawberry100};
  padding: 8px 0 0;
`;

type CampaignSchedulingEditProps = {
  timezone: string;
  timeframe: SchedulingFormValues['timeframe'];
  schedules: SchedulingFormValues['schedules'];
  errors?: ValidationErrors;
  isExistingRunningCampaign: boolean;
  onCloseClick(): void;
  onSubmit(): void;
  onScheduleChange(schedules: SchedulingFormValues['schedules']): void;
  onTimeframeChange(timeframe: SchedulingFormValues['timeframe']): void;
};

const SchedulingFormModal = ({
  onSubmit,
  timezone,
  timeframe,
  schedules,
  errors,
  isExistingRunningCampaign,
  onCloseClick,
  onScheduleChange,
  onTimeframeChange,
}: CampaignSchedulingEditProps) => {
  const { locale } = useIntl();
  const disabledDays = {
    before: DateTime.now().setZone(timezone).toJSDate(),
  };

  return (
    <StyledWrapper data-test-id="scheduling-form">
      <ModalHeader onCloseClick={onCloseClick}>
        <FormattedMessage id="campaigns.form.scheduling.label" />
      </ModalHeader>
      <StyledContentWrapper>
        <StyledSection>
          <StyledTitle>
            <FormattedMessage id="campaigns.form.scheduling.dates.label" />
          </StyledTitle>

          <StyledSectionContent>
            <Field<SchedulingFormValues['startDate']> name="startDate" type="date">
              {({ input }) => (
                <DateTimePicker
                  {...input}
                  locale={locale}
                  value={DateTime.fromISO(input.value).setZone(timezone)}
                  onChange={value => input.onChange(value?.toISO())}
                  timezone={timezone}
                  isInvalid={!!errors?.startDate}
                  disabled={isExistingRunningCampaign}
                  disabledDays={disabledDays}
                />
              )}
            </Field>

            <Field<SchedulingFormValues['endDate']> name="endDate" type="date">
              {({ input }) => (
                <DateTimePicker
                  {...input}
                  locale={locale}
                  value={DateTime.fromISO(input.value).setZone(timezone)}
                  onChange={value => input.onChange(value?.toISO())}
                  timezone={timezone}
                  disabledDays={disabledDays}
                  isInvalid={!!errors?.endDate}
                />
              )}
            </Field>
          </StyledSectionContent>
          {(!!errors?.startDate || !!errors?.endDate) && (
            <StyledErrorMessage>
              <FormattedMessage id={errors.startDate || errors.endDate} />
            </StyledErrorMessage>
          )}
        </StyledSection>

        {!isExistingRunningCampaign && (
          <>
            <StyledSection>
              <StyledTitle>
                <FormattedMessage id="campaigns.form.scheduling.timeframes.label" />
              </StyledTitle>
              <StyledSectionContent>
                {SCHEDULING_TIMEFRAMES.map(timeframe => (
                  <Field<SchedulingFormValues['timeframe']>
                    name="timeframe"
                    type="radio"
                    value={timeframe.value}
                    key={timeframe.value}
                  >
                    {({ input }) => (
                      <CustomRadioCard
                        {...input}
                        onChange={() => {
                          input.onChange(input.value);
                          onTimeframeChange(input.value);
                        }}
                      >
                        <Typography.Body3>
                          <FormattedMessage id={timeframe.label} />
                        </Typography.Body3>
                      </CustomRadioCard>
                    )}
                  </Field>
                ))}
              </StyledSectionContent>
            </StyledSection>

            {timeframe === 'specific-times' && (
              <SchedulesSection
                schedules={schedules}
                errors={errors?.schedules?.invalidSections}
                onScheduleChange={onScheduleChange}
                onTimeframeChange={onTimeframeChange}
              />
            )}
          </>
        )}
      </StyledContentWrapper>
      <StyledFooter>
        <Button variant="primary" size="small" onClick={onSubmit}>
          <FormattedMessage id="campaigns.form.update" />
        </Button>
      </StyledFooter>
    </StyledWrapper>
  );
};

export default SchedulingFormModal;
